/* eslint-disable class-methods-use-this */
/* eslint-disable prefer-destructuring */
/**
 * A class that holds a solitary subscription
 */
export class Subscription {
  /**
   * @param {object} options
   * @param {string[]} options.key
   * @param {function(...*): void} options.callback
   * @param {function(): void} options.unsubscribe
   */
  constructor(options) {
    this.key = options.key;
    this.callback = options.callback.bind(this);
    this.unsubscribe = options.unsubscribeFn.bind(this);
  }

  /**
   * Calls the provided callback function with supplied args.
   * @param  {...*} args that are passed ot the callback.
   * @return {void}
   */
  trigger(...args) {
    setTimeout(() => {
      this.callback(...args);
    }, 0);
  }
}

/**
 * A SubscriptionProvider Class. The base type of any subscription is this
 * provider.  By supplying Subscription Objects this class will manage their
 * lifecyle.
 * @return {SubscriptionProvider}
 */
export const SubscriptionProvider = (() => {
  // poorman's privates
  const subscriptions = Symbol('subscriptions');
  const buildSubscriptionFn = Symbol('buildSubscriptionFn');

  /**
   * Remove from a subscription object from the subscription Provider
   * @param  {Subscription[]} _subscriptions The collection of suscriptions.
   * @param  {Subscription} subscription     The subscription in which to remove.
   * @return {void}
   */
  const removeFromSubscriptions = (_subscriptions, subscription) => {
    const arrEq = (firstArray, secondArray) => firstArray.every((val, index) => val === secondArray[index]);
    const index = _subscriptions.findIndex((sub) => arrEq(sub.key, subscription.key));
    if (index !== -1) {
      _subscriptions.splice(index, 1);
    }
  };

  /**
   * Triggers a subscription callback for a provided collections of subscription
   * objects.
   * @param  {Subscription} subscriptionsToTrigger The subscriptions in which to trigger.
   * @param  {Array} args                          The arguments to provide to
   *                                               each callback.
   * @return {void}
   */
  const trigger = (subscriptionsToTrigger, ...args) => {
    subscriptionsToTrigger.forEach((s) => {
      s.trigger(...args);
    });
  };

  class SubscriptionProviderClass {
    constructor(options) {
      const _options = Object.assign({
        /* put defaults in here */
      }, options);

      this[buildSubscriptionFn] = _options.buildSubscriptionFn;
      this[subscriptions] = [];
    }

    /**
     * Triggers a subscription event, by calling the callback for all registered
     * subscription objects that match the key.
     * @param  {string} key  The key in which to match subscriptions
     * @param  {Array}  args The args in which to supply to the callbacks
     * @return {void}
     */
    trigger(key, ...args) {
      trigger(this[subscriptions].filter((s) => s.key === key), ...args);
    }

    /**
     * Triggers every callback,regardless of their keys.
     * @param  {string} key  The key in which to match subscriptions
     * @return {void}
     */
    triggerAll(...args) {
      trigger(this[subscriptions], ...args);
    }

    /**
     * Subscribes a function to a particular key.
     * @param  {Function} fn  A callback that will execute when the subscriptionProvider
     *                        recieves a trigger event with same key.
     * @param  {string}   key The thing that this thing is subscribing too.
     * @return {Function}     An unsubscribe function.
     */
    subscribe(fn, key) {
      const subscription = this[buildSubscriptionFn](fn, key);
      const unsubscribe = subscription.unsubscribe;

      subscription.unsubscribe =
        () => {
          removeFromSubscriptions(this[subscriptions], subscription);
          unsubscribe.call(subscription);
        };

      this[subscriptions].push(subscription);

      return subscription.unsubscribe.bind(subscription);
    }

    /**
     * Unsubscribes from all registered subscriptions.
     */
    unsubscribeAll() {
      while (this[subscriptions].length) {
        this[subscriptions][this[subscriptions].length - 1].unsubscribe();
      }
    }
  }

  return SubscriptionProviderClass;
})();
